import { ALL_STORES, DB_NAME, DB_VERSION } from './constants';
import type { StoreNames } from './types';

// Use globalThis.indexedDB which works in both main thread and Vite workers
const idb = globalThis.indexedDB;

/**
 * Initialize the IndexedDB database with the complete schema.
 * Always creates all stores to prevent partial schema issues.
 */
export const initDB = () => {
  return new Promise<boolean>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;

      // if the data object store doesn't exist, create it
      ALL_STORES.forEach(config => {
        if (!db.objectStoreNames.contains(config.storeName)) {
          const store = db.createObjectStore(config.storeName, {
            keyPath: config.keyPath,
            autoIncrement: config.autoIncrement
          });
          const indices = config.indices ?? [];
          for (const {
            indexName,
            keyPath,
            options
          } of indices) {
            store.createIndex(indexName, keyPath, options);
          }
        }
      });
      // no need to resolve here
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      db.close();
      resolve(true);
    };
    request.onblocked = () => {
      console.warn('[IDB] Database upgrade blocked by another connection');
    };
    request.onerror = () => {
      reject(false);
    };
  });
};
export const setData = <T,>(storeName: StoreNames, data: T) => {
  return new Promise<T | DOMException | null>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.put(data);
      tx.oncomplete = () => {
        db.close();
        resolve(data);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error('Transaction failed'));
      };
      tx.onabort = () => {
        db.close();
        reject(new Error('Transaction aborted'));
      };
    };
    request.onerror = () => {
      const error = request.error;
      if (error) {
        reject(error);
      } else {
        reject('Unknown error');
      }
    };
  });
};
export const setBulkData = <T,>(storeName: StoreNames, dataArray: T[]) => {
  return new Promise<boolean>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      dataArray.forEach(data => {
        store.put(data);
      });
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        console.error('[IDB] setBulkData transaction error:', tx.error);
        db.close();
        reject(tx.error || new Error('Transaction failed'));
      };
      tx.onabort = () => {
        console.error('[IDB] setBulkData transaction aborted');
        db.close();
        reject(new Error('Transaction aborted'));
      };
    };
    request.onerror = () => {
      console.error('[IDB] setBulkData failed to open DB:', request.error);
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const getAllData = <T,>(storeName: StoreNames) => {
  return new Promise<T[]>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const res = store.getAll();
      res.onsuccess = () => {
        db.close();
        resolve(res.result);
      };
      res.onerror = error => {
        db.close();
        reject(error);
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const getDataByKey = <T,>(storeName: StoreNames, key: IDBValidKey) => {
  return new Promise<T | null>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const res = store.get(key);
      res.onsuccess = () => {
        db.close();
        resolve(res.result || null);
      };
      res.onerror = error => {
        db.close();
        reject(error);
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const getBulkDataByKeys = <T,>(storeName: StoreNames, keys: IDBValidKey[]) => {
  return new Promise<T[]>((resolve, reject) => {
    if (keys.length === 0) {
      resolve([]);
      return;
    }
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);

      // Use getAll with key range for efficient bulk retrieval
      const keyRange = IDBKeyRange.bound(keys[0], keys[keys.length - 1]);
      const getAllRequest = store.getAll(keyRange);
      getAllRequest.onsuccess = () => {
        db.close();
        resolve(getAllRequest.result || []);
      };
      getAllRequest.onerror = () => {
        db.close();
        reject(getAllRequest.error || new Error('Failed to get bulk data'));
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const deleteData = (storeName: StoreNames, key: IDBValidKey) => {
  return new Promise<boolean>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.delete(key);
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error('Transaction failed'));
      };
      tx.onabort = () => {
        db.close();
        reject(new Error('Transaction aborted'));
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const deleteAllData = (storeName: StoreNames) => {
  return new Promise<boolean>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.clear();
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error('Transaction failed'));
      };
      tx.onabort = () => {
        db.close();
        reject(new Error('Transaction aborted'));
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const deleteBulkData = (storeName: StoreNames, keys: IDBValidKey[]) => {
  return new Promise<boolean>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      keys.forEach(key => {
        store.delete(key);
      });
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error('Transaction failed'));
      };
      tx.onabort = () => {
        db.close();
        reject(new Error('Transaction aborted'));
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const getDataByRange = <T,>(storeName: StoreNames, lowerBound: IDBValidKey, upperBound: IDBValidKey): Promise<T[]> => {
  return new Promise<T[]>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const range = IDBKeyRange.bound(lowerBound, upperBound);
      const res = store.getAll(range);
      res.onsuccess = () => {
        db.close();
        resolve(res.result || []);
      };
      res.onerror = () => {
        db.close();
        reject(res.error || new Error('Failed to get data by range'));
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};
export const deleteDataByRange = (storeName: StoreNames, lowerBound: IDBValidKey, upperBound: IDBValidKey): Promise<boolean> => {
  return new Promise<boolean>((resolve, reject) => {
    const request: IDBOpenDBRequest = idb.open(DB_NAME, DB_VERSION);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.delete(IDBKeyRange.bound(lowerBound, upperBound));
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error('Transaction failed'));
      };
      tx.onabort = () => {
        db.close();
        reject(new Error('Transaction aborted'));
      };
    };
    request.onerror = () => {
      reject(request.error || new Error('Failed to open database'));
    };
  });
};